import {
  Flex,
  Text,
  Image,
  Box,
  Heading,
  useTheme,
  Icon,
  Stack,
  Link,
  IconName
} from '@homebotapp/hb-react-component-catalog'
import { AppLayout } from '../../../App/Layout/AppLayout'
import { useIntl } from 'react-intl'
import { defineMessages } from 'react-intl'
import useViewport from '../../../hooks/useViewport'
import marketInsights from '../assets/market-insights.png'
import lightMarketInsights from '../assets/light-market-insights.png'
import marketInsightsSpanish from '../assets/market-insights-es.png'
import lightMarketInsightsSpanish from '../assets/light-market-insights-es.png'
import curatedGalleries from '../assets/gallery.png'
import lightCuratedGalleries from '../assets/light-gallery.png'
import gallerySpanish from '../assets/gallery-es.png'
import lightGallerySpanish from '../assets/light-gallery-es.png'

const MSG = defineMessages({
  title: {
    id: 'ForYouContent.impersonationPage.title',
    defaultMessage: 'For You'
  },
  noPreview: {
    id: 'ForYouContent.impersonationPage.no.preview',
    defaultMessage: "You can't preview this page until your client gets it started"
  },
  noPreviewDescription: {
    id: 'ForYouContent.impersonationPage.noPreviewDescription',
    defaultMessage: 'Make sure the details are filled out on their profile, then encourage them to explore!'
  },
  marketInsightsHeading: {
    id: 'ForYouContent.impersonationPage.marketInsightsHeading',
    defaultMessage: 'Market Insights'
  },
  marketInsightsDescription: {
    id: 'ForYouContent.impersonationPage.marketInsightsDescription',
    defaultMessage:
      'Your clients can see rich data about their favorite markets, personalized to their budget. You stay front and center whenever they want to know more.'
  },
  curatedGalleriesHeading: {
    id: 'ForYouContent.impersonationPage.curatedGalleriesHeading',
    defaultMessage: 'Curated Galleries'
  },
  curatedGalleriesDescription: {
    id: 'ForYouContent.impersonationPage.curatedGalleriesDescription',
    defaultMessage:
      'The most interesting and relevant listings in each favorite market. Clients can get alerts when we find more homes to share.'
  },
  learnMoreAboutThisLink: {
    id: 'ForYouContent.impersonationPage.learnMoreAboutThisLink',
    defaultMessage: 'Learn more about Home Search'
  }
})

const GALLERY_TAGS_MSG = defineMessages({
  newlyListed: {
    id: 'ForYouContent.impersonationPage.galleryTags.newlyListed',
    defaultMessage: 'Newly Listed'
  },
  priceDrops: {
    id: 'ForYouContent.impersonationPage.galleryTags.priceDrops',
    defaultMessage: 'Price Drops'
  },
  recentlySold: {
    id: 'ForYouContent.impersonationPage.galleryTags.recentlySold',
    defaultMessage: 'Recently Sold'
  },
  openHouses: {
    id: 'ForYouContent.impersonationPage.galleryTags.openHouses',
    defaultMessage: 'Open Houses'
  },
  sellerConcessions: {
    id: 'ForYouContent.impersonationPage.galleryTags.sellerConcessions',
    defaultMessage: 'Seller Concessions'
  },
  assumableLoans: {
    id: 'ForYouContent.impersonationPage.galleryTags.assumableLoans',
    defaultMessage: 'Assumable Loans'
  }
})

const galleryTagsData: Array<{ iconName: IconName; messageKey: keyof typeof GALLERY_TAGS_MSG }> = [
  {
    iconName: 'home',
    messageKey: 'newlyListed'
  },
  {
    iconName: 'home',
    messageKey: 'priceDrops'
  },
  {
    iconName: 'home',
    messageKey: 'recentlySold'
  },
  {
    iconName: 'home',
    messageKey: 'openHouses'
  },
  {
    iconName: 'home',
    messageKey: 'sellerConcessions'
  },
  {
    iconName: 'home',
    messageKey: 'assumableLoans'
  }
]

export const ImpersonationPage = ({ match }) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const { isLargeOrAbove } = useViewport()

  const isSpanish = intl.locale === 'es'
  const isLightTheme = theme.isLightThemeType

  const marketInsightsImage = isSpanish
    ? isLightTheme
      ? lightMarketInsightsSpanish
      : marketInsightsSpanish
    : isLightTheme
    ? lightMarketInsights
    : marketInsights

  const curatedGalleriesImage = isSpanish
    ? isLightTheme
      ? lightGallerySpanish
      : gallerySpanish
    : isLightTheme
    ? lightCuratedGalleries
    : curatedGalleries

  return (
    <AppLayout loading={false} enableAgentDropdown={false} match={match}>
      <Box
        mt={-2}
        py={[5, null, 12]}
        px={[5, null, 20]}
        backgroundSize='cover'
        borderBottom='2px solid'
        borderColor='neutral.200'
        backgroundBlendMode='multiply'
        backgroundColor='whiteAlpha.50'
        _dark={{
          backgroundColor: 'blackAlpha.600'
        }}
      >
        <Box>
          <Heading as='h1' size='2xl' textAlign='left' fontWeight={600} m={0}>
            {intl.formatMessage(MSG.title)}
          </Heading>
        </Box>
      </Box>

      <Box px={isLargeOrAbove ? 20 : 4} mt={8} mb={20}>
        {/* @ts-ignore */}
        <Box bgColor='brand.200 !important' p={4} borderRadius='md' border='1px solid var(--hb-colors-brand-300)'>
          <Flex flexDirection={isLargeOrAbove ? 'row' : 'column'} gap={4} alignItems='center' justifyContent='center'>
            <Box bgColor='brand.100' px={3} pt={2.5} pb={2} borderRadius='50%'>
              <Icon name='eye-entypo' color='brand.500' width={6} height={6} />
            </Box>
            <Stack gap={0} textAlign={isLargeOrAbove ? 'left' : 'center'}>
              <Heading
                mb={isLargeOrAbove ? 0 : 1}
                size={isLargeOrAbove ? 'md' : 'sm'}
                textAlign={isLargeOrAbove ? 'left' : 'center'}
              >
                {intl.formatMessage(MSG.noPreview)}
              </Heading>
              <Text mb={0} size={isLargeOrAbove ? 'md' : 'sm'} textAlign={isLargeOrAbove ? 'left' : 'center'}>
                {intl.formatMessage(MSG.noPreviewDescription)}
              </Text>
            </Stack>
          </Flex>
        </Box>

        <Box mt={8}>
          <Flex flexDirection={isLargeOrAbove ? 'row' : 'column'} gap={8}>
            <Box flex={1}>
              <Heading size='lg' textAlign='left'>
                {intl.formatMessage(MSG.marketInsightsHeading)}
              </Heading>
              <Text size={isLargeOrAbove ? 'lg' : 'md'}>{intl.formatMessage(MSG.marketInsightsDescription)}</Text>
            </Box>
            <Box flex={1} alignItems='flex-end'>
              <Flex justifyContent={isLargeOrAbove ? 'flex-end' : 'center'}>
                <Image src={marketInsightsImage} aria-hidden='true' />
              </Flex>
            </Box>
          </Flex>

          <Flex flexDirection={isLargeOrAbove ? 'row' : 'column-reverse'} gap={8} mb={8} mt={10}>
            <Box flex={1}>
              <Flex justifyContent={isLargeOrAbove ? 'flex-start' : 'center'}>
                <Image src={curatedGalleriesImage} aria-hidden='true' />
              </Flex>
            </Box>
            <Box flex={1}>
              <Stack
                justifyContent={isLargeOrAbove ? 'flex-end' : 'flex-start'}
                alignItems={isLargeOrAbove ? 'flex-end' : 'flex-start'}
              >
                <Heading size='lg' textAlign='left'>
                  {intl.formatMessage(MSG.curatedGalleriesHeading)}
                </Heading>
                <Text size={isLargeOrAbove ? 'lg' : 'md'} textAlign={isLargeOrAbove ? 'right' : 'left'}>
                  {intl.formatMessage(MSG.curatedGalleriesDescription)}
                </Text>
                <Box>
                  <Flex
                    flexWrap='wrap'
                    gap={4}
                    alignItems='center'
                    justifyContent={isLargeOrAbove ? 'flex-end' : 'flex-start'}
                  >
                    {galleryTagsData.map((tag, index) => (
                      <GalleryTags
                        key={index}
                        iconName={tag.iconName}
                        label={intl.formatMessage(GALLERY_TAGS_MSG[tag.messageKey])}
                      />
                    ))}
                  </Flex>
                </Box>
              </Stack>
            </Box>
          </Flex>
        </Box>

        <Flex justifyContent={isLargeOrAbove ? 'flex-end' : 'center'} pb={isLargeOrAbove ? 20 : 24}>
          <Link
            href='https://help.homebotapp.com/en/articles/5753529-homebot-home-search'
            isExternal
            mb='.125rem'
            size={isLargeOrAbove ? 'lg' : 'md'}
            tracking={{
              ui_context: 'For_you_page_impersonation',
              descriptor: `Clicked on Learn more about home search`,
              guid: `noEkQTv9gUrTftZ23i73qx`
            }}
          >
            {intl.formatMessage(MSG.learnMoreAboutThisLink)}
            <Icon name='external-link' ml={1} />
          </Link>
        </Flex>
      </Box>
    </AppLayout>
  )
}

const GalleryTags = ({ iconName, label }: { iconName: IconName; label: string }) => {
  const { isLargeOrAbove } = useViewport()

  return (
    <Flex
      alignItems='center'
      gap={2}
      color='neutral.700'
      bgColor='neutral.50'
      paddingY={isLargeOrAbove ? 2 : 1}
      paddingX={isLargeOrAbove ? 3 : 2}
      borderRadius={16}
      borderColor='neutral.200'
      borderWidth={1}
    >
      <Icon color='brand.700' name={iconName} />
      <Text mb={0} size={isLargeOrAbove ? 'sm' : 'xs'}>
        {label}
      </Text>
    </Flex>
  )
}
